import { Box, Theme } from '@mui/material';
import { SystemStyleObject } from '@mui/system';
import { FC, MouseEvent as ReactMouseEvent, useCallback, useEffect, useRef, useState } from 'react';
import ReactGA from 'react-ga4';

import {
  MAX_PANEL_WIDTH, MIN_PANEL_WIDTH, RESIZE_HANDLE_WIDTH, clampPanelWidth,
} from './panelWidth';

type Props = {
  /** どちら側のパネルを動かすか。left は右へ引くほど広がる */
  side: 'left' | 'right';
  /** 現在の幅。ドラッグ開始時の基準にする */
  width: number;
  /** ダブルクリックで戻す幅 */
  defaultWidth: number;
  /** 読み上げと操作の識別に使う名前 */
  label: string;
  /**
   * 操作ログの画面名。/map とカード配車で共有するので、ここを固定値にすると
   * 片方の操作がもう片方の画面として集計される。
   */
  screenName: string;
  /** clamp 済みの幅を渡す。ドラッグ中は移動のたびに呼ぶ */
  onWidthChange: (width: number) => void;
  /** 掴んでいる間だけ true。幅の transition を止めるのに使う */
  onResizingChange?: (resizing: boolean) => void;
  /**
   * 3 ペインを並べている器の実測幅。0（まだ測れていない）なら上限の逆算をしない。
   *
   * Why not ハンドルの親要素を自分で測る: カード配車では左ハンドルの親が横スクロールする
   * 行（minWidth: max-content）で、器ではなく内容の幅が返る。上限が効かなくなる。
   * 両画面とも器を ResizeObserver で測っているので、その値をもらう。
   */
  containerWidth?: number;
  /**
   * このパネル以外に確保しておく幅（反対側のパネル＋ハンドル＋中央ペインの最小幅）。
   * 渡すと、器の幅から逆算した上限でドラッグを止める（panelWidth.reservedWidthFor）。
   */
  reservedWidth?: number;
  /**
   * 置き場所に依存する見た目の上書き。
   * カード配車の左ハンドルは横スクロールする器の中にあり、左カラム (sticky left: 0) の
   * 隣へ貼り付けないと横スクロールしたときだけ掴めなくなるので sticky を足す。
   *
   * Why not SxProps: 配列も関数も受ける型なので、合成に spread が要り any が混ざる。
   * ここで欲しいのは素のスタイルオブジェクト 1 つだけ。
   */
  sx?: SystemStyleObject<Theme>;
};

/**
 * 左右リストの境界に置くリサイズハンドル。/map とカード配車が共有する。
 * サンプル (route-planner: pages/MapPage.tsx startLeftResize / startRightResize) と同じく
 * ドラッグで幅を変え、ダブルクリックで既定幅に戻す。
 *
 * Why window に張る: ハンドルは 6px しかないので、少し速く動かすとポインタが要素の外へ出る。
 * 要素の mousemove だけを見ていると追従が途切れる。
 */
const PanelResizeHandle: FC<Props> = ({
  side, width, defaultWidth, label, screenName, onWidthChange, onResizingChange,
  containerWidth = 0, reservedWidth, sx,
}) => {
  const [resizing, setResizing] = useState(false);
  /** ドラッグ中の後片付け。mouseup とアンマウントの両方から同じ処理を呼ぶ */
  const detachRef = useRef<(() => void) | null>(null);

  useEffect(() => () => detachRef.current?.(), []);

  const onMouseDown = useCallback((event: ReactMouseEvent) => {
    // 押した時点でテキスト選択やドラッグ開始が始まると、掴んだ瞬間に選択が走る
    event.preventDefault();
    ReactGA.event('click', { screen_name: screenName, button_name: `${label}（ドラッグ）` });

    const startX = event.clientX;
    const startWidth = width;

    // 上限は「器の幅 − 確保しておく幅」でも抑える。
    // jsdom など幅が測れない場所では 0 が渡るので、その場合は既定の上限だけを使う
    const maxWidth = reservedWidth != null && containerWidth > 0
      ? Math.max(MIN_PANEL_WIDTH, Math.min(MAX_PANEL_WIDTH, containerWidth - reservedWidth))
      : MAX_PANEL_WIDTH;

    const onMove = (moveEvent: globalThis.MouseEvent) => {
      const delta = side === 'left' ? moveEvent.clientX - startX : startX - moveEvent.clientX;
      onWidthChange(Math.min(maxWidth, clampPanelWidth(startWidth + delta)));
    };
    const detach = () => {
      detachRef.current = null;
      setResizing(false);
      onResizingChange?.(false);
      window.removeEventListener('mousemove', onMove);
      window.removeEventListener('mouseup', detach);
      window.removeEventListener('blur', detach);
      document.body.style.cursor = '';
      document.body.style.userSelect = '';
    };

    detachRef.current = detach;
    setResizing(true);
    onResizingChange?.(true);
    // 掴んだまま中央ペインやリストの上を通っても、カーソルと選択状態が変わらないようにする
    document.body.style.cursor = 'col-resize';
    document.body.style.userSelect = 'none';
    window.addEventListener('mousemove', onMove);
    window.addEventListener('mouseup', detach);
    // ドラッグ中に Cmd+Tab などでフォーカスを失うと mouseup を取り逃す。追従が残ると
    // 戻ってきて動かしただけで幅が変わり続け、body の文字選択も止まったままになる
    window.addEventListener('blur', detach);
  }, [containerWidth, label, onResizingChange, onWidthChange, reservedWidth, screenName, side, width]);

  const onDoubleClick = useCallback(() => {
    ReactGA.event('click', { screen_name: screenName, button_name: `${label}（既定に戻す）` });
    onWidthChange(defaultWidth);
  }, [defaultWidth, label, onWidthChange, screenName]);

  return (
    <Box
      role="separator"
      aria-orientation="vertical"
      aria-label={label}
      onMouseDown={onMouseDown}
      onDoubleClick={onDoubleClick}
      sx={[
        {
          // 細すぎると掴めず、太いと境界線に見えない
          width: RESIZE_HANDLE_WIDTH,
          flexShrink: 0,
          cursor: 'col-resize',
          // 掴める場所だと分かるのは hover / ドラッグ中だけ。常時色を置くと境界線が二重に見える
          bgcolor: resizing ? 'hsl(var(--primary) / 0.35)' : 'transparent',
          transition: 'background-color 0.15s',
          '&:hover': { bgcolor: 'hsl(var(--primary) / 0.25)' },
        },
        // 配列で渡すと後ろが勝つ（MUI の sx 合成）。呼び出し側の上書きを効かせる
        sx ?? {},
      ]}
    />
  );
};

export default PanelResizeHandle;
